import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useLocation } from "wouter";
import { ArrowLeft, Check, Gift, Loader2, Plus } from "lucide-react";
import { useWalletBalance, useTopUpPackages, useTopUpWallet } from "@/hooks/use-wallet";
import { useTheme } from "@/components/ThemeProvider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import knetLogo from "@assets/knet-logo.png";
import creditCardLogos from "@assets/credit-card-logos.jpg";
import applePayLogoLight from "../assets/images/apple_pay_light.png";
import applePayLogoDark from "../assets/images/apple_pay_dark.png";

export default function TopUpWallet() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [, navigate] = useLocation();
  const { theme } = useTheme();

  const { data: walletData } = useWalletBalance();
  const balance = walletData?.balance ?? 0;
  const currency = walletData?.currency ?? t("services.currency");

  const { data: topUpPackages = [] } = useTopUpPackages();
  const topUpMutation = useTopUpWallet();
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'knet' | 'credit_card' | 'apple_pay' | 'online'>('online');

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        <button
          onClick={() => navigate("/wallet")}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          {isRtl ? <ArrowLeft className="h-4 w-4 rotate-180" /> : <ArrowLeft className="h-4 w-4" />}
          {t("walletPage.title")}
        </button>

        <h1 className="text-2xl font-bold mb-2">{t("walletPage.topUp")}</h1>
        <p className="text-sm text-muted-foreground mb-6">
          {t("walletPage.balance")}: {balance.toFixed(3)} {currency}
        </p>

        {topUpPackages.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-2xl border border-border/50">
            <p className="text-muted-foreground">{t("walletPage.noPackages")}</p>
          </div>
        ) : (
          <>
            {/* Packages + Payment in one card */}
            <Card className="rounded-xl border border-border/50 p-6 mb-6">
              <Label className="mb-3 block">{t("walletPage.selectPackage")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {topUpPackages.map((pkg, index) => {
                  const isSelected = selectedPackage === index;
                  const bonus = pkg.credit - pkg.price;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSelectedPackage(isSelected ? null : index)}
                      aria-pressed={isSelected}
                      className={cn(
                        "relative text-start p-4 rounded-xl border-2 transition-all",
                        isSelected
                          ? "border-primary bg-primary/5 ring-4 ring-primary/10"
                          : "border-border hover:border-primary/50 hover:bg-muted/30",
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-bold text-sm">{isRtl ? pkg.nameAr : pkg.nameEn}</p>
                        <span
                          className={cn(
                            "shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors",
                            isSelected ? "border-primary bg-primary" : "border-muted-foreground/30",
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </span>
                      </div>

                      {/* What actually lands in the wallet — the headline number */}
                      <p className="font-bold text-2xl text-primary mt-2 leading-none">
                        {pkg.credit.toFixed(3)} {currency}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{t("walletPage.creditAdded")}</p>

                      {bonus > 0 && (
                        <span className="inline-flex items-center gap-1 mt-3 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-semibold text-green-600 dark:text-green-400">
                          <Gift className="h-3.5 w-3.5" />
                          +{bonus.toFixed(3)} {currency}
                        </span>
                      )}

                      <div className="mt-3 pt-3 border-t border-border/50 flex items-baseline justify-between gap-2">
                        <span className="text-xs text-muted-foreground">{t("walletPage.pay")}</span>
                        <span className="font-semibold text-sm">
                          {pkg.price.toFixed(3)} {currency}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Payment method — inline, same row as packages section */}
              <div className="border-t border-border/50 pt-4">
                <Label className="mb-3 block">{t("giftPage.paymentMethod")}</Label>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("online")}
                  aria-pressed={paymentMethod === "online"}
                  className={cn(
                    "w-full flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 transition-all",
                    paymentMethod === "online"
                      ? "border-primary bg-primary/5 ring-4 ring-primary/10"
                      : "border-border hover:border-primary/50 hover:bg-muted/30",
                  )}
                >
                  <div className="flex items-center justify-center gap-2">
                    <img src={knetLogo} alt="KNET" className="h-6 object-contain rounded" />
                    <img src={creditCardLogos} alt="Visa / Mastercard" className="h-6 object-contain rounded" />
                    <img src={theme === "dark" ? applePayLogoDark : applePayLogoLight} alt="Apple Pay" className="h-6 object-contain" />
                  </div>
                  <span
                    className={cn(
                      "text-[11px] font-medium leading-none",
                      paymentMethod === "online" ? "text-primary" : "text-muted-foreground",
                    )}
                  >
                    {t("booking.payment.online")}
                  </span>
                  <span className="text-[10px] text-muted-foreground text-center">{t("booking.payment.onlineHint")}</span>
                </button>
                <p className="text-xs text-muted-foreground mt-2">{t("booking.payment.secureNote")}</p>
              </div>
            </Card>

            {/* Order summary — only once a package is picked, so the customer
                sees exactly what they pay and what lands in the wallet. */}
            {selectedPackage !== null && (() => {
              const pkg = topUpPackages[selectedPackage];
              if (!pkg) return null;
              const bonus = pkg.credit - pkg.price;
              const methodLabel =
                paymentMethod === "knet"
                  ? t("booking.payment.knet")
                  : paymentMethod === "credit_card"
                    ? t("booking.payment.creditCard")
                    : paymentMethod === "apple_pay"
                      ? t("booking.payment.applePay")
                      : t("booking.payment.online");
              const amount = (v: number) => `${v.toFixed(3)} ${currency}`;

              return (
                <Card className="rounded-xl border border-border/50 p-6 mb-6" data-testid="topup-summary">
                  <Label className="mb-3 block">{t("booking.orderSummary")}</Label>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t("walletPage.package")}</span>
                      <span className="font-medium text-end">{isRtl ? pkg.nameAr : pkg.nameEn}</span>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t("walletPage.creditAdded")}</span>
                      <span className="font-medium">{amount(pkg.credit)}</span>
                    </div>

                    {bonus > 0 && (
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-muted-foreground">{t("walletPage.bonusCredit")}</span>
                        <span className="font-medium text-green-600 dark:text-green-400">
                          +{amount(bonus)}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t("giftPage.paymentMethod")}</span>
                      <span className="font-medium">{methodLabel}</span>
                    </div>

                    <div className="border-t border-border/50 pt-2 mt-2 flex items-center justify-between gap-4">
                      <span className="font-semibold">{t("booking.payment.amountToPay")}</span>
                      <span className="font-bold text-lg text-primary" data-testid="topup-total">
                        {amount(pkg.price)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
                      <span>{t("walletPage.newBalance")}</span>
                      <span>{amount(balance + pkg.credit)}</span>
                    </div>
                  </div>
                </Card>
              );
            })()}

            {/* Confirm button */}
            {selectedPackage !== null && (
              <Button
                type="button"
                size="lg"
                className="w-full h-14 text-lg font-semibold rounded-xl"
                disabled={topUpMutation.isPending}
                onClick={() => {
                  topUpMutation.mutate(
                    {
                      packageIndex: selectedPackage,
                      packageId: (topUpPackages[selectedPackage] as any)?._id,
                      paymentMethod,
                    },
                    {
                      onSuccess: (data) => {
                        if (data.paymentLink) {
                          window.location.href = data.paymentLink;
                        }
                      },
                    },
                  );
                }}
              >
                {topUpMutation.isPending ? (
                  <>
                    <Loader2 className="me-2 h-5 w-5 animate-spin" />
                    {t("booking.processing")}
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5 me-2" />
                    {t("walletPage.topUpNow")}
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
